/**
 * NaamDarbar — Protected Route Component
 *
 * Wraps routes that require authentication. Redirects to /login if not authenticated.
 * Optionally enforces a minimum role (user, ragi, admin).
 */

import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

interface ProtectedRouteProps {
  children: React.ReactNode;
  /** Minimum role required. Defaults to 'user' (any authenticated user). */
  requireRole?: 'user' | 'raagi' | 'admin';
}

export default function ProtectedRoute({
  children,
  requireRole = 'user',
}: ProtectedRouteProps) {
  const { isAuthenticated, isRagi, isAdmin, loading } = useAuth();
  const location = useLocation();

  // Still checking auth state — show nothing (or a spinner)
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-kesri-600" />
      </div>
    );
  }

  // Not authenticated at all
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check role
  if (requireRole === 'admin' && !isAdmin) {
    return <Navigate to="/" replace />;
  }
  if (requireRole === 'raagi' && !isRagi) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}
